import fetch from 'cross-fetch';
import humps from 'humps';

import ErrorTracker from 'error_tracker';
import { isEmpty } from 'core/utils/objectUtils';

import { buildUrl } from './utils';

export default class ApiRequest {
	static defaultOptions = {
		displayErrors: true,
		autoHumps: true,
		includeCredentials: true,
	};

	/**
	 * Create an API Request Object
	 * @param method {string} get|post|put|delete|head|options (optional, default: get)
	 * @param endpoint {string}
	 */
	static create(method, endpoint, data, options) {
		return new this(method, endpoint, data, options);
	}

	static get(endpoint, data = {}, options = {}) {
		return ApiRequest.create('get', endpoint, data, options).send();
	}

	static put(endpoint, data = {}, options = {}) {
		return ApiRequest.create('put', endpoint, data, options).send();
	}

	static post(endpoint, data = {}, options = {}) {
		return ApiRequest.create('post', endpoint, data, options).send();
	}

	static delete(endpoint, data = {}, options = {}) {
		return ApiRequest.create('delete', endpoint, data, options).send();
	}

	static patch(endpoint, data = {}, options = {}) {
		return ApiRequest.create('patch', endpoint, data, options).send();
	}

	/**
	 * Initialize a new ApiRequest object
	 * @param  {string} method
	 * @param  {string} endpoint
	 * @param  {object} data
	 * @param  {object} options
	 * @return {object} ApiRequest
	 */
	constructor(method = 'get', endpoint = '/', data = {}, options = {}) {
		if (endpoint.indexOf('?') !== -1) {
			throw new Error('You must set query string data via the `query` method');
		}

		this.options = {
			...ApiRequest.defaultOptions,
			...options,
		};

		this.url = endpoint;
		this.queryParams = {};

		this.reqOpts = {
			// patch needs to be uppercase because reasons, so just make them all uppercase
			// see: https://github.com/github/fetch/issues/254
			method: method.toUpperCase(),
			compress: false,
			headers: null,
			body: undefined,
		};

		if (this.options.includeCredentials) {
			this.reqOpts.credentials = 'include';
		}

		this.headers({
			Accept: 'application/json',
			'Content-Type': 'application/json',
		});

		// Set query or body data
		if (!isEmpty(data)) {
			if (this.reqOpts.method === 'GET' || this.reqOpts.method === 'DELETE') {
				this.query(data);
			} else {
				this.body(data);
			}
		}

		return this;
	}

	/**
	 * Makes Async Ajax Api Call
	 * @return {Promise}
	 */
	send = () => {
		return new Promise((resolve, reject) => {
			// Do things like:
			// - clear previous error states
			// - update network request indicator

			this.url = buildUrl(this.url, this.queryParams);

			if (!isEmpty(this.reqOpts.body)) {
				let body = this.reqOpts.body;
				if (this.options.autoHumps) {
					body = humps.decamelizeKeys(this.reqOpts.body, { separator: '_' });
				}
				this.reqOpts.body = JSON.stringify(body);
			}

			// Fetch URL with given options:
			// console.log(this.url, this.reqOpts);
			fetch(this.url, this.reqOpts)
				.then(response => {
					if (response.ok && response.status === 204) {
						resolve({});
						return;
					}
					if (
						response.ok &&
						response.redirected &&
						response.url.endsWith('/login')
					) {
						// Safety Dance -- In case 401 actually returns the redirect to /login
						// Would happen if you call an API endpoint that's not under the /api/ umbrella
						return reject({
							fetchResponse: { status: 401 },
							errors: ['Login required'],
						});
					}
					response
						.json()
						.then(data => {
							if (response.ok) {
								resolve(
									this.options.autoHumps ? humps.camelizeKeys(data) : data
								);
								return;
							}

							// Network response was not OK:
							// inspect response.status if you want to,
							// for example, log out user who get 401
							if (this.options.displayErrors) {
								// do some common error handling
								// if error structure is predictable
							}
							// In case you want to inspect the response
							// for things like status === 401
							data.fetchResponse = response;
							reject(data);
						})
						.catch(e => {
							// 404 or errors that dont return JSON would come in here
							ErrorTracker.captureException(e);
							reject(e);
						});
				})
				// Network error: usually permission issues or wifi droppped (not 404)
				.catch(reject);
		});
	};

	// -------------------
	// Helper Functions
	// -------------------

	body(data) {
		let b = this.reqOpts.body || {};
		this.reqOpts.body = {
			...b,
			...data,
		};

		return this;
	}

	query(data) {
		this.queryParams = {
			...this.queryParams,
			...data,
		};

		return this;
	}

	headers(headers = {}) {
		let h = this.reqOpts.headers || {};
		this.reqOpts.headers = {
			...h,
			...headers,
		};

		return this;
	}
}
